/* File: frontend/src/components/reputation/AuditRunner.js */
/* Version: 2.4 (Golden Master) */
/* Date: Monday, December 8, 2025 at 12:20 PM EST */
/* Description: Handles audit execution, polling, and result handling.
   Updates:
   - Fixed ID extraction logic to handle multiple backend response formats.
   - Added robust error handling and UI feedback states.
   - Integrated safe polling with useRef cleanup.
*/

import React, { useState, useEffect, useRef } from 'react';
import { Play, CheckCircle, AlertTriangle, Loader, X } from 'lucide-react';
import reputationService from '../../services/reputationService';

const AuditRunner = ({ initialCompanyName, initialUrl, profileId, onAuditComplete }) => {
  // State Management
  const [status, setStatus] = useState('idle'); // idle, running, polling, success, error
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [taskId, setTaskId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  
  // Ref for polling interval to clear it safely
  const pollInterval = useRef(null);

  // Helper to add timestamped logs
  const addLog = (msg) => {
      setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
  };

  // --- START AUDIT FUNCTION ---
  const startAudit = async () => {
    setStatus('running');
    setLogs([]);
    setProgress(10);
    setErrorMsg('');
    
    const targetName = initialCompanyName || 'Profile';
    addLog(`Initiating audit for ${targetName}...`);

    try {
      // 1. Determine execution mode (Profile vs Ad-hoc)
      let data;
      
      if (profileId) {
        // Run audit for an existing saved profile
        data = await reputationService.runAuditForProfile(profileId);
      } else {
        // Run ad-hoc audit from dashboard input
        data = await reputationService.runAudit({ 
            company_name: initialCompanyName, 
            company_url: initialUrl 
        });
      }

      // 2. Handle Server Response
      if (data.task_id) {
        setTaskId(data.task_id);
        setStatus('polling');
        addLog(`Task started (ID: ${data.task_id}). Waiting for AI analysis...`);
      } else {
        throw new Error('No Task ID returned from server.');
      }

    } catch (err) {
      console.error(err);
      setStatus('error');
      setErrorMsg(err.message || 'Failed to start audit.');
      addLog(`Error: ${err.message}`);
    }
  };

  // --- POLLING LOGIC ---
  useEffect(() => {
    // Only poll if we have a Task ID and status is polling
    if (status === 'polling' && taskId) {
        
      pollInterval.current = setInterval(async () => {
        try {
          const data = await reputationService.getTaskStatus(taskId);
          
          if (data.status === 'processing' || data.status === 'pending') {
             // Fake progress while waiting (up to 90%)
             setProgress(prev => Math.min(prev + 5, 90)); 
             
             // Log backend messages if available
             if (data.message && !logs.includes(data.message)) {
                 // You can uncomment this to see backend logs in UI
                 // addLog(data.message);
             }
          } 
          else if (data.status === 'completed' || data.status === 'success') {
             // --- SUCCESS CASE ---
             clearInterval(pollInterval.current);
             setStatus('success');
             setProgress(100);
             addLog('Audit completed successfully!');
             
             // [CRITICAL FIX] ROBUST ID EXTRACTION
             // We look for the ID in multiple places to avoid "undefined"
             const resultAuditId = 
                data.result?.audit_id || 
                data.result?.id || 
                data.audit_id;
             
             if (!resultAuditId) {
                 console.warn("[AuditRunner] Finished but ID not found in response:", data);
                 addLog("Warning: Audit ID missing in response.");
             } else {
                 console.log("[AuditRunner] Found ID:", resultAuditId);
             }

             // Delay slightly to let user see 100% bar
             setTimeout(() => {
                 if (onAuditComplete) {
                     onAuditComplete({ 
                         auditId: resultAuditId, 
                         status: 'success' 
                     });
                 }
             }, 1500);
          } 
          else if (data.status === 'failed') {
             // --- FAILURE CASE ---
             clearInterval(pollInterval.current);
             setStatus('error');
             setErrorMsg(data.error || 'Audit failed on server.');
             addLog(`Failed: ${data.error}`);
          }
        } catch (e) {
          console.error("Polling error", e);
          // Don't stop polling on single network error, just wait for next tick
        }
      }, 2000); // Poll every 2 seconds
    }

    // Cleanup on unmount or status change
    return () => {
        if (pollInterval.current) {
            clearInterval(pollInterval.current);
        }
    };
  }, [status, taskId, onAuditComplete, logs]);

  // --- RENDER ---
  return (
    <div className="card" style={{ 
        padding: '2rem', 
        textAlign: 'center',
        minHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    }}>
      <h3 style={{ marginBottom: '1.5rem', color: '#1e293b' }}>
          AI Reputation Audit
      </h3>
      
      {/* STATE: IDLE */}
      {status === 'idle' && (
        <div>
          <p style={{ 
              color: '#64748b', 
              marginBottom: '2rem', 
              lineHeight: '1.6' 
          }}>
            Analyze <b>{initialCompanyName || 'this company'}</b> using GPT-4o.<br/>
            This process will check Google, analyze sentiment, and find gaps.<br/>
            Estimated time: 30-60 seconds.
          </p>
          <button 
            className="btn btn-primary btn-lg" 
            onClick={startAudit} 
            style={{ 
                width: '100%', 
                justifyContent: 'center',
                padding: '12px'
            }}
          >
            <Play size={20} style={{ marginRight: '8px' }} /> 
            Start Audit
          </button>
        </div>
      )}

      {/* STATE: RUNNING / POLLING */}
      {(status === 'running' || status === 'polling') && (
        <div>
          <div style={{ marginBottom: '1.5rem' }}>
             <Loader 
                className="spin" 
                size={48} 
                color="#3b82f6" 
                style={{ margin: '0 auto' }} 
             />
          </div>
          
          <h4 style={{ color: '#3b82f6', marginBottom: '10px' }}>
              Analyzing Reputation... {progress}%
          </h4>
          
          <div className="progress-bar-container" style={{ 
              margin: '20px 0', 
              height: '8px', 
              background: '#e2e8f0', 
              borderRadius: '4px',
              overflow: 'hidden'
          }}>
             <div 
                style={{ 
                    width: `${progress}%`, 
                    height: '100%', 
                    background: '#3b82f6', 
                    borderRadius: '4px', 
                    transition: 'width 0.5s ease-in-out' 
                }}
             ></div>
          </div>
          
          <div className="audit-logs" style={{ 
              textAlign: 'left', 
              background: '#f8fafc', 
              padding: '15px', 
              borderRadius: '8px', 
              fontSize: '0.85rem', 
              color: '#64748b', 
              maxHeight: '180px', 
              overflowY: 'auto',
              border: '1px solid #e2e8f0'
          }}>
             {logs.map((l, i) => (
                 <div key={i} style={{ marginBottom: '4px' }}>{l}</div>
             ))}
          </div>
        </div>
      )}

      {/* STATE: SUCCESS */}
      {status === 'success' && (
        <div className="animate-fade-in">
           <CheckCircle 
                size={64} 
                color="#10b981" 
                style={{ margin: '0 auto 1.5rem' }} 
           />
           <h4 style={{ 
               color: '#10b981', 
               fontSize: '1.5rem', 
               marginBottom: '1rem' 
           }}>
               Audit Complete!
           </h4>
           <p style={{ color: '#64748b' }}>
               Redirecting to results dashboard...
           </p>
        </div>
      )}

      {/* STATE: ERROR */}
      {status === 'error' && (
        <div>
           <AlertTriangle 
                size={56} 
                color="#ef4444" 
                style={{ margin: '0 auto 1.5rem' }} 
           />
           <h4 style={{ 
               color: '#ef4444', 
               fontSize: '1.25rem', 
               marginBottom: '1rem' 
           }}>
               Audit Failed
           </h4>
           <p style={{ 
               color: '#991b1b', 
               marginBottom: '2rem',
               background: '#fee2e2',
               padding: '10px',
               borderRadius: '6px'
           }}>
               {errorMsg}
           </p>
           <button 
                className="btn btn-secondary" 
                onClick={() => setStatus('idle')}
                style={{ width: '100%' }}
           >
               Try Again
           </button>
        </div>
      )}
    </div>
  );
};

export default AuditRunner;